//
// # Identifiers
//
// The means of identifying schematics, symbols, and similar pieces of content.
//

// NPM Imports
import typia from "typia";
import { schema } from "GenAlphaSchema";
import { exhaust, Validator } from "./errors";

// Re-export all the schema types
export type Ident = schema.Ident;
export type BuiltinIdent = schema.BuiltinIdent;
export type LibCellViewIdent = schema.LibCellViewIdent;
export type BuiltinId = schema.BuiltinId;
export type LibCellView = schema.LibCellView;
export type UuidIdent = schema.UuidIdent;
export type HierPathIdent = schema.HierPathIdent;

// A "spec" for a built-in, i.e. from raw user input.
// Hasn't been verified that `id` is one of the valid values.
export type BuiltinIdentSpec = {
  kind: "builtin";
  id: string; //
};

// "Spec" for an identifier. Generally before it's been validated.
export type IdentSpec = BuiltinIdentSpec | NonBuiltinIdent;

// # Non-Builtin (i.e. user-defined) Identifiers
export type NonBuiltinIdent = LibCellViewIdent | UuidIdent | HierPathIdent;

export const Ident = {
  // Create a default Ident (empty hierpath)
  default: (): HierPathIdent => ({ kind: "hierpath", path: [] }),

  // Create a built-in Ident
  builtin: (id: BuiltinId): BuiltinIdent => ({ kind: "builtin", id }),

  // Create a `LibCellView` based `Ident`
  libCellView: (libCellView: LibCellView): LibCellViewIdent => ({
    kind: "libcellview",
    ...libCellView,
  }),

  // Get a name to be displayed
  displayName: (ident: Ident): string => {
    const { kind } = ident;
    switch (kind) {
      case "libcellview":
        // Show the full lib/cell/view path (parallels the hierpath case's last
        // three components) so OpenAccess designs get a complete tab title
        // rather than just the cell name.
        return `${ident.lib} / ${ident.cell} / ${ident.view}`;
      case "builtin":
        return ident.id;
      case "uuid":
        return ident.name;
      case "hierpath": // XXX TODOL align on what to display here
        return ident.path.length > 0
          ? ident.path.slice(-3).join(" / ")
          : "no_name";
      case null:
        return "null";
      default:
        exhaust(kind);
    }
  },

  // URL-safe path form, each segment URI-encoded. Used as the design-render
  // storage key.
  pathName: (ident: Ident): string => {
    const seg = (s: string): string => encodeURIComponent(s);
    const { kind } = ident;
    switch (kind) {
      case "hierpath":
        return ["hierpath", ...ident.path].map(seg).join("/");
      case "libcellview":
        return ["libcellview", ident.lib, ident.cell, ident.view]
          .map(seg)
          .join("/");
      case "uuid":
        return ["uuid", ident.id].map(seg).join("/");
      case "builtin":
        return ["builtin", ident.id].map(seg).join("/");
      default:
        return exhaust(kind);
    }
  },

  // Equality comparison
  // Compares two Idents for structural equality.
  // Use this instead of === or manual stringify comparisons.
  equals: (a: Ident, b: Ident): boolean =>
    Ident.stringify(a) === Ident.stringify(b),

  // JSON validation
  validator: new Validator(typia.createValidate<Ident>()),
  stringify: typia.json.createStringify<Ident>(),
};

export const NonBuiltinIdent = {
  // The hash key used for indexing in several contexts
  key: (ident: NonBuiltinIdent): string => Ident.stringify(ident),
};

export const LibCellView = {
  // The "null" `LibCellView`
  null: (): LibCellView => ({ lib: "", cell: "", view: "" }),
  // Create a `LibCellView` based `Ident`
  ident: (lcv: LibCellView): Ident => Ident.libCellView(lcv),
};

interface HasIdent {
  ident: Ident;
}

export class IdentMap<T extends HasIdent> {
  private inner = new Map<string, T>();

  private static key(ident: Ident): string {
    return Ident.stringify(ident);
  }

  has(ident: Ident): boolean {
    return this.inner.has(IdentMap.key(ident));
  }

  get(ident: Ident): T | undefined {
    return this.inner.get(IdentMap.key(ident));
  }

  set(value: T): void {
    this.inner.set(IdentMap.key(value.ident), value);
  }

  delete(ident: Ident): boolean {
    return this.inner.delete(IdentMap.key(ident));
  }

  clear(): void {
    this.inner.clear();
  }

  get size(): number {
    return this.inner.size;
  }

  values(): IterableIterator<T> {
    return this.inner.values();
  }
}

export class IdentSet {
  // Note the inner thing is a Map, so that we can keep the Idents around
  private inner = new Map<string, Ident>();

  private static key(ident: Ident): string {
    return Ident.stringify(ident);
  }

  has(ident: Ident): boolean {
    return this.inner.has(IdentSet.key(ident));
  }

  add(ident: Ident): unknown {
    return this.inner.set(IdentSet.key(ident), ident);
  }

  delete(ident: Ident): boolean {
    return this.inner.delete(IdentSet.key(ident));
  }

  clear(): void {
    this.inner.clear();
  }

  // Convert to an array
  toArray(): Array<Ident> {
    return Array.from(this.inner.values());
  }
}
